import './DodFactSetBuilder.scss';
import React, { ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import classnames from 'classnames';
import { ByzzerChangeEventHandler } from '@byzzer/ui-components';
import DodFactValuePickerTable from '@/components/DodConfigEditor/builders/DodFactSetBuilder/DodFactValuePickerTable/DodFactValuePickerTable';
import { DodPanel, DodPanelRef } from '@/components/DodConfigEditor/common/DodPanel';
import { alert as byzzerAlert } from '@/components/form';
import { useDodWizard } from '@/components/DodConfigEditor/DodRunConfigWizard/DodWizardContext';
import { DodFactTypePicker } from './DodFactTypePicker';
import { DodFactSetPreview } from './DodFactSetPreview';
import { DodFactSet, DodFilters } from '@/types/DodRun';
import { useUser, useApp } from '@/contexts/UserContext';
import { useEventDataWithUserInfo, useTrackEvent } from '@/analytics/AnalyticsContext';
import { DodPreset } from '@/types/ApiTypes';
import { DodFactFilterType, DodFilterType } from '../../types';
import { filterHasValues, isWorkInProgressFactsDisabled } from '../../common/utils';
import { useLink } from '@/hooks/useLinks';
import { LINK_CODES, PAGE_CODES } from '@/constants/links.constants';

const baseClassName = 'dod-fact-set-builder';

export type DodFactSetBuilderProps = {
    className?: string;
    name?: string;
    tip?: ReactNode;
    filters: DodFilters;
    value: DodFactSet[];
    onChange: ByzzerChangeEventHandler<DodFactSet[]>;
    onValidityChange?(e: ByzzerValidityChangeEvent): void;
} & Partial<Omit<React.HTMLAttributes<HTMLDivElement>, 'onChange'>>;

export function DodFactSetBuilder({
    className,
    name,
    value,
    onChange,
    filters,
    onValidityChange,
    tip,
    ...props
}: DodFactSetBuilderProps) {

    const { subscription, features } = useUser()
    const selectionLimit = subscription?.metadata?.extractLimits?.facts ?? Infinity
    const trackEvent = useTrackEvent();
    const getEventData = useEventDataWithUserInfo();
    const { factConfig } = useApp();
    const factTriggerRef = useRef<HTMLDivElement>(null);

    const [filterText, setFilterText] = useState<string>('');
    const [filterType, setFilterType] = useState<DodFilterType<DodFactFilterType>>({
        type: 'All Facts',
        data: {
            type: "core"
        }
    });
    const valuePanelRef = useRef<DodPanelRef>(null);

    const { runConfig, presets, rowColConfigs, deletePreset, setRowColConfigs } = useDodWizard();
    const [acvArticleLinkData] = useLink([{ linkCode: LINK_CODES.ACV_ARTICLE, pageCode: PAGE_CODES.DOD_FACTS_STEP }]);
    const extendedDataYears = useMemo(() => {
        return features?.extendedDataYears === 5;
    }, [features]);

    useEffect(() => {
        const atleastOneFactSelected: boolean = value.some(
            (fact) =>
                fact.fact ||
                fact.yearAgo ||
                fact.changeVsYearAgo ||
                fact.twoYearAgo ||
                fact.changeVsTwoYearAgo ||
                fact.percentChangeVsTwoYearAgo ||
                fact.threeYearAgo ||
                fact.changeVsThreeYearAgo ||
                fact.percentChangeVsThreeYearAgo ||
                fact.fourYearAgo ||
                fact.changeVsFourYearAgo ||
                fact.percentChangeVsFourYearAgo ||
                fact.percentChangeVsYearAgo ||
                fact.category ||
                fact.categoryYearAgo ||
                fact.categoryTwoYearAgo ||
                fact.categoryThreeYearAgo ||
                fact.categoryFourYearAgo ||
                fact.brand ||
                fact.brandYearAgo ||
                fact.brandTwoYearAgo ||
                fact.brandThreeYearAgo ||
                fact.brandFourYearAgo ||
                fact.productSelections ||
                fact.productSelectionsYearAgo ||
                fact.productSelectionsTwoYearAgo ||
                fact.productSelectionsThreeYearAgo ||
                fact.productSelectionsFourYearAgo ||
                fact.subCategory ||
                fact.subCategoryYearAgo || 
                fact.subCategoryTwoYearAgo || 
                fact.subCategoryThreeYearAgo ||
                fact.subCategoryFourYearAgo
        );
        onValidityChange?.({
            name,
            isValid: Boolean(value.length && atleastOneFactSelected),
        });
    }, [value]);

    function handleFilterTypeChange(e) {
        if (e.value?.type?.toLowerCase() !== 'all facts') {
            trackEvent({
                type: 'click',
                name: 'dod_filter_type_click',
                data: getEventData({
                    dodWizardStep: 'fact',
                    panel: 'value picker',
                    filterType: e.value?.type,
                    folder: e.value?.data?.type,
                }),
            });
        }
        if (e.value.type === "mySavedFacts") {
            onChange({ name, value: e.value.data })
        } else {
            setFilterType(e.value);
        }

        valuePanelRef.current?.resetFilter();
    }

    function handleFilterValueChange(e: ByzzerChangeEvent<DodFactSet[]>, keepPresets?: boolean) {
        // * remove selected preset values
        if (presets?.fact && !keepPresets) {
            deletePreset('fact');
        }

        validateLayoutHideCheckbox(e.value);

        let updatedValue: DodFactSet[] = value ?? [];
        updatedValue = e.value;
        onChange({
            name,
            value: updatedValue,
        });
    }

    function validateLayoutHideCheckbox(facts: DodFactSet[]) {
        let isCategoryFact = false;
        let isBrandFact = false;
        let isUPCFact = false;
        let isUPCHidden = false;
        let isSubcategoryFact = false;

        for (const fact of facts) {
            if (fact.category || fact.categoryYearAgo || fact.categoryTwoYearAgo || fact.categoryThreeYearAgo || fact.categoryFourYearAgo) isCategoryFact = true;
            if (fact.brand || fact.brandYearAgo || fact.brandTwoYearAgo || fact.brandThreeYearAgo || fact.brandFourYearAgo) isBrandFact = true;
            if (fact.subCategory || fact.subCategoryYearAgo || fact.subCategoryTwoYearAgo || fact.subCategoryThreeYearAgo || fact.subCategoryFourYearAgo) isSubcategoryFact = true;
            if (fact.isUPCrequired) isUPCFact = true;

            if (isCategoryFact && isBrandFact && isUPCFact) break;
        }
        if (isCategoryFact || isBrandFact || isUPCFact || isSubcategoryFact) {
            setRowColConfigs((rowColConfigs) => {
                let updated = false;
                const newConfigs = rowColConfigs.map((d) => {
                    if (d.type === 'products') {
                        if (d.dim === 'categories' && isCategoryFact && d.hide) {
                            d.hide = false;
                            updated = true;
                        }
                        if (d.dim === 'brands' && isBrandFact && d.hide) {
                            d.hide = false;
                            updated = true;
                        }
                        if (d.dim === 'upcs' && isUPCFact && d.hide) {
                            d.hide = false;
                            updated = true;
                            isUPCHidden = true;
                        }
                        if (d.dim === 'subcategories' && isSubcategoryFact && d.hide) {
                            d.hide = false;
                            updated = true;
                        }
                    }
                    return d;
                });

                return updated ? newConfigs : rowColConfigs;
            });
        }
        if (isUPCHidden) {
            byzzerAlert({
                content: <div>Adding this fact will unhide UPC in your layout selections panel.</div>,
            });
        }
    }

    function validateFactNames(facts: DodFactSet[]): DodFactSet[] {
        let validatedFacts: DodFactSet[] = [];
        const allFacts: DodFactSet[] = factConfig.core.find((v) => v.value === 'All Facts').factLists;
        validatedFacts = facts
            .map((fact) => {
                let foundFact = allFacts.find((ft) => ft.id === Number(fact.id));
                if (foundFact) {
                    return { ...fact, display: foundFact.display };
                } else {
                    return undefined;
                }
            })
            .filter((fact) => fact !== undefined);
        return validatedFacts;
    }

    function handlePresetSelect({ value }: ByzzerChangeEvent<DodPreset<DodFactSet[]>>) {

        const isProductStacked = rowColConfigs.some((config) => config.type === 'products' && config.stack);
        const isCategoryStacked = rowColConfigs.some((config) => config.type === 'products' && config.dim === 'categories' && config.stack);
        const isBrandStacked = isProductStacked && rowColConfigs.some((config) => config.type === 'products' && config.dim === 'brands' && config.stack);
        const isSubcategoryStacked = isProductStacked && rowColConfigs.some((config) => config.type === 'products' && config.dim === 'subcategories' && config.stack);

        // upc facts need only to check if upc is selected or not
        const upcFactsWithoutUPCSelection =
            value.values.some((fact) => fact.isUPCrequired) && !filterHasValues(filters.upcs);
        // brand facts need that either brand or upc be selected, hence checking filter values of both brand and upcs
        const brandFactsWithoutBrandOrUPCSelection =
            value.values.some((fact) => fact.brand || fact.brandYearAgo || fact.brandTwoYearAgo || fact.brandThreeYearAgo || fact.brandFourYearAgo) &&
            !filterHasValues(filters.brands) &&
            !filterHasValues(filters.upcs);

        const subCategoryFactsWithoutSubcategorySelection =
            value.values.some((fact) => fact.subCategory || fact.subCategoryYearAgo || fact.subCategoryTwoYearAgo || fact.subCategoryThreeYearAgo || fact.subCategoryFourYearAgo) &&
            !filterHasValues(filters.subcategories)

        const brandFactsWithoutBrandSelection = 
            value.values.some((fact) => fact.brand || fact.brandYearAgo || fact.brandTwoYearAgo || fact.brandThreeYearAgo || fact.brandFourYearAgo) &&
            !filterHasValues(filters.brands);

        const categoryFactsWithCategoryStacked =
            value.values.some((fact) => fact.category || fact.categoryYearAgo || fact.categoryTwoYearAgo || fact.categoryThreeYearAgo || fact.categoryFourYearAgo) &&
            isCategoryStacked;

        const brandFactsWithBrandStacked =
            value.values.some((fact) => fact.brand || fact.brandYearAgo || fact.brandTwoYearAgo || fact.brandThreeYearAgo || fact.brandFourYearAgo) &&
            isBrandStacked;

        const subCategoryFactsWithSubcategoryStacked = value.values.some((fact) => fact.subCategory || fact.subCategoryYearAgo || fact.subCategoryTwoYearAgo || fact.subCategoryThreeYearAgo || fact.subCategoryFourYearAgo) &&
            isSubcategoryStacked;

        const productSelectionsFactsWithProductSelectionsStacked =
            value.values.some((fact) => fact.productSelections || fact.productSelectionsYearAgo || fact.productSelectionsTwoYearAgo || fact.productSelectionsThreeYearAgo || fact.productSelectionsFourYearAgo) &&
            isProductStacked;

        let updatedFactValues = value.values.filter((fact) =>
            !isWorkInProgressFactsDisabled({ fact, runConfig, rowColConfigs })
        );

        if (upcFactsWithoutUPCSelection || brandFactsWithoutBrandOrUPCSelection || subCategoryFactsWithoutSubcategorySelection) {
            let alertMessage = '';

            if (upcFactsWithoutUPCSelection && brandFactsWithoutBrandOrUPCSelection && subCategoryFactsWithoutSubcategorySelection) {
                alertMessage =
                    'This saved facts list contains Brand-level, SubCategory-level and UPC-level only facts, but you do not have SubCategory or UPC or Brand included in your selections. These facts will not be added.';
            } else if (brandFactsWithoutBrandOrUPCSelection && subCategoryFactsWithoutSubcategorySelection) {
                alertMessage =
                    'This saved facts list contains Brand-level and SubCategory-level facts, but you do not have SubCategory or UPC or Brand included in your selections. These facts will not be added.';
            } else if (upcFactsWithoutUPCSelection && subCategoryFactsWithoutSubcategorySelection) {
                alertMessage =
                    'This saved facts list contains UPC-level and SubCategory-level facts, but you do not have SubCategory or UPC included in your selections. These facts will not be added.';
            } else if (upcFactsWithoutUPCSelection && brandFactsWithoutBrandOrUPCSelection) {
                alertMessage =
                    'This saved facts list contains Brand-level and UPC-level only facts, but you do not have UPC or Brand included in your selections. These facts will not be added.';
            } else if (upcFactsWithoutUPCSelection) {
                alertMessage =
                    'This saved facts list contains UPC-level only facts, but you do not have UPC included in your selections. These facts will not be added.';
            } else if (subCategoryFactsWithoutSubcategorySelection) {
                alertMessage =
                    'This saved facts list contains SubCategory-level facts, but you do not have SubCategory included in your selections. These facts will not be added.'; 
            } else {
                alertMessage =
                    'This saved facts list contains Brand-level facts, but you do not have Brand or UPC included in your selections. These facts will not be added.';
            }

            byzzerAlert({ content: <div>{alertMessage}</div> });

            // if only upc facts were selected without upc being selected OR
            // if both brand and upc level facts are selected without brand or upc selection
            if ((upcFactsWithoutUPCSelection && brandFactsWithoutBrandOrUPCSelection) || upcFactsWithoutUPCSelection) {
                // remove upc level facts
                updatedFactValues = updatedFactValues.filter((fact) => !fact.isUPCrequired);
                // if no brand selection, remove brand level facts also (since upc selection can enable to select brand facts also)
                if (!filterHasValues(runConfig.filters.brands)) {
                    updatedFactValues = updatedFactValues.map((fact) => ({
                        ...fact,
                        brand: false,
                        brandYearAgo: false,
                        brandTwoYearAgo: false,
                        brandThreeYearAgo: false,
                        brandFourYearAgo: false
                    }));
                }
            } else if (brandFactsWithoutBrandSelection) { // if only brand facts were selected without brand or upc being selected
                updatedFactValues = updatedFactValues.map((fact) => ({
                    ...fact,
                    brand: false,
                    brandYearAgo: false,
                    brandTwoYearAgo: false,
                    brandThreeYearAgo: false,
                    brandFourYearAgo: false
                }));
            }

            if(subCategoryFactsWithoutSubcategorySelection) {
                 updatedFactValues = updatedFactValues.map((fact) => ({
                    ...fact,
                    subCategory: false,
                    subCategoryYearAgo: false,
                    subCategoryTwoYearAgo: false,
                    subCategoryThreeYearAgo: false,
                    subCategoryFourYearAgo: false
                }));
            }
        }

        if(categoryFactsWithCategoryStacked || brandFactsWithBrandStacked || subCategoryFactsWithSubcategoryStacked || productSelectionsFactsWithProductSelectionsStacked) {
            if(categoryFactsWithCategoryStacked) {
                updatedFactValues = updatedFactValues.map((fact) => ({
                    ...fact,
                    category: false,
                    categoryYearAgo: false,
                    categoryTwoYearAgo: false,
                    categoryThreeYearAgo: false,
                    categoryFourYearAgo: false
                }));
            }

            if(brandFactsWithBrandStacked) {
                updatedFactValues = updatedFactValues.map((fact) => ({
                    ...fact,
                    brand: false,
                    brandYearAgo: false,
                    brandTwoYearAgo: false,
                    brandThreeYearAgo: false,
                    brandFourYearAgo: false
                }));
            }

            if(subCategoryFactsWithSubcategoryStacked) {
                updatedFactValues = updatedFactValues.map((fact) => ({
                    ...fact,
                    subCategory: false,
                    subCategoryYearAgo: false,
                    subCategoryTwoYearAgo: false,
                    subCategoryThreeYearAgo: false,
                    subCategoryFourYearAgo: false
                }));
            }

            if(productSelectionsFactsWithProductSelectionsStacked) {
                updatedFactValues = updatedFactValues.map((fact) => ({
                    ...fact,
                    productSelections: false,
                    productSelectionsYearAgo: false,
                    productSelectionsTwoYearAgo: false,
                    productSelectionsThreeYearAgo: false,
                    productSelectionsFourYearAgo: false
                }));
            }
        }

        if(!extendedDataYears) {
            // remove 3-4 year ago facts if extended data years is not enabled
            updatedFactValues = updatedFactValues.map((fact) => ({
                ...fact,
                threeYearAgo: false,
                changeVsThreeYearAgo: false,
                percentChangeVsThreeYearAgo: false,
                categoryThreeYearAgo: false,
                brandThreeYearAgo: false,
                productSelectionsThreeYearAgo: false,
                subCategoryThreeYearAgo: false,
                fourYearAgo: false,
                changeVsFourYearAgo: false,
                percentChangeVsFourYearAgo: false,
                categoryFourYearAgo: false,
                brandFourYearAgo: false,
                productSelectionsFourYearAgo: false,
                subCategoryFourYearAgo: false
            }));
            
        }
        // remove all facts that are not valid
        updatedFactValues = updatedFactValues.filter(
            (fact) =>
                fact.fact ||
                fact.yearAgo ||
                fact.changeVsYearAgo ||
                fact.twoYearAgo ||
                fact.changeVsTwoYearAgo ||
                fact.percentChangeVsTwoYearAgo ||
                fact.threeYearAgo ||
                fact.changeVsThreeYearAgo ||
                fact.percentChangeVsThreeYearAgo ||
                fact.fourYearAgo ||
                fact.changeVsFourYearAgo ||
                fact.percentChangeVsFourYearAgo ||
                fact.percentChangeVsYearAgo ||
                fact.category ||
                fact.categoryYearAgo ||
                fact.categoryTwoYearAgo ||
                fact.categoryThreeYearAgo ||
                fact.categoryFourYearAgo ||
                fact.brand ||
                fact.brandYearAgo ||
                fact.brandTwoYearAgo ||
                fact.brandThreeYearAgo ||
                fact.brandFourYearAgo ||
                fact.productSelections ||
                fact.productSelectionsYearAgo ||
                fact.productSelectionsTwoYearAgo ||
                fact.productSelectionsThreeYearAgo ||
                fact.productSelectionsFourYearAgo ||
                fact.subCategory ||
                fact.subCategoryYearAgo ||
                fact.subCategoryTwoYearAgo ||
                fact.subCategoryThreeYearAgo ||
                fact.subCategoryFourYearAgo
        );

        handleFilterValueChange(
            {
                name,
                value: validateFactNames(updatedFactValues),
            },
            true
        );
    }

    return <div className={classnames(baseClassName, className)} {...props}>
        <DodFactTypePicker value={filterType}
            onChange={handleFilterTypeChange}
            showPresetWarning={Boolean(value.length)}
            onPresetSelect={handlePresetSelect} />
        <DodPanel expandable={true}
            byzRef={valuePanelRef}
            filterPlaceholder={'Search Facts'}
            onFilterChange={setFilterText}
            name={'dod-facts-picker'}
            enableColumnHideShow={true}
            factTriggerRef={factTriggerRef}
            trackClick={{ data: { dodWizardStep: "fact", panel: "value picker" } }}
        >
            <DodFactValuePickerTable
                value={value}
                onApply={handleFilterValueChange}
                filterType={filterType}
                filterText={filterText}
                filters={filters}
                limit={selectionLimit}
                factTriggerRef={factTriggerRef}
                acvArticleLinkData={acvArticleLinkData}
            />

        </DodPanel>
        <DodFactSetPreview
            value={value}
            onChange={onChange}
            name={name}
            emptyStateContent={'No Facts Selected'}
        />
    </div>
}

export default DodFactSetBuilder;
